import { useState } from 'react';
import { AddToShoppingCartContext, IProduct } from '@monofront/types';
import ButtonAddToCart from '@components/ButtonAddToCart';
import { useAuthentication } from '@context/businessContainer';
import { HIDDEN_PRICE } from '@monofront/business/constants';
import {
  getMaxQuantity,
  getCurrentProductQuantity,
  getIsAntiWaste,
  getMinimumProductQuantity,
} from '@monofront/business/utils';
import { useShoppingCart } from '@hooks/cart/useShoppingCart';
import { useMediaQuery } from '@monofront/business/hooks';
import CTAContainer from '@components/ProductInformations/CTA';
import NotifyMeButton from '@components/NotifyMeButton';
import { useTranslations } from 'use-intl';
import AddCart from '@monofront/design-system/visuals/Icons/Old/AddCart';
import { useFormatProductWeight } from '@monofront/business/hooks/product/useFormatProductWeight';

interface ICTAProps {
  product: IProduct;
  isSticky?: boolean;
  addToShoppingCartContext?: AddToShoppingCartContext;
}

const CTA: React.FC<ICTAProps> = ({
  product,
  isSticky = false,
  addToShoppingCartContext,
}) => {
  const [isNotifyMeModalOpen, setIsNotifyMeModalOpen] = useState(false);
  const { isAuthenticated } = useAuthentication();
  const { shoppingCart } = useShoppingCart();
  const isMobile = useMediaQuery(800);
  const isSmallDesktop = useMediaQuery(1024);

  const { formatProductWeight } = useFormatProductWeight();
  const tCatalog = useTranslations('Catalog');
  const tCart = useTranslations('Cart');

  const isPriceHidden =
    !isAuthenticated &&
    product.tags?.some((tag) => tag === HIDDEN_PRICE.STARTWITH);

  const maxQuantity = getMaxQuantity(product);
  const currentProductQuantity = getCurrentProductQuantity(
    product,
    shoppingCart.products,
  );
  const isAntiWaste = getIsAntiWaste(product.tags);
  const formattedProductWeight = formatProductWeight(
    product.meta.finance,
    false,
  );

  const minimumQuantity = getMinimumProductQuantity(product.tags);
  const hasEnoughStock =
    product.inventory_quantity >= getMinimumProductQuantity(product.tags);

  product.inventory_available = hasEnoughStock;

  return (
    <>
      <CTAContainer
        danger={isAntiWaste}
        title={product.title}
        weight={formattedProductWeight}
        price={product.price}
        comparedAtPrice={product.compare_at_price}
        shouldHidePrice={isPriceHidden}
        isStickyBottomScreen={isSticky}
        isMaxHeight={minimumQuantity !== 1 && currentProductQuantity > 0}
        ctaButton={
          (product.inventory_quantity <= 0 && !isAntiWaste) ||
          minimumQuantity > product.inventory_quantity ? (
            <NotifyMeButton
              productTitle={product.title}
              sku={product.sku}
              productTags={product.tags}
              inventoryAvailable={product.inventory_available}
              isNotifyMeModalOpen={isNotifyMeModalOpen}
              setIsNotifyMeModalOpen={setIsNotifyMeModalOpen}
            />
          ) : (
            <div className="button-container">
              <ButtonAddToCart
                shoppingCartProduct={product}
                max={maxQuantity}
                size={isMobile ? 'medium' : 'large'}
                hasEditViewOnly={true}
                isDisabled={isAntiWaste && product.inventory_quantity <= 0}
                containerContext={addToShoppingCartContext}
              >
                <div className="button-content">
                  <AddCart width={24} height={24} />
                  {!isSmallDesktop && (
                    <span className="button-text">
                      {tCart('addToCartConfirm')}
                    </span>
                  )}
                </div>
              </ButtonAddToCart>
              {product.inventory_quantity > 0 &&
                minimumQuantity === 1 &&
                maxQuantity <= currentProductQuantity &&
                minimumQuantity === 1 && (
                  <p className="max-quantity-reached">
                    {tCatalog('endOfStock')}
                  </p>
                )}
              {currentProductQuantity > 0 && minimumQuantity > 1 && (
                <p className="tw-lf-caption-regular tw-pt-[2px] tw-text-center tw-text-black-700">
                  {tCart('minimumProductAmount', {
                    minimumQuantity,
                  })}
                </p>
              )}
            </div>
          )
        }
      />
      <style jsx>{`
        .max-quantity-reached {
          color: var(--clr-danger);
          left: 0;
          font-size: var(--font-size-1);
          font-weight: var(--font-weight-bold);
          position: absolute;
          right: 0;
          text-align: center;
        }

        .button-container {
          position: relative;
        }
        .button-container :global(> button) {
          padding: var(--space-3) var(--space-5);
        }

        .button-content {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: var(--space-2);
        }

        .button-text {
          white-space: nowrap;
          text-overflow: ellipsis;
          font-size: var(--font-size-2);
        }
      `}</style>
    </>
  );
};

export default CTA;
